import type { UploadMode } from '../../../shared/protocol.ts';
import { Observable } from './emitter.ts';
import type { RateSource } from './uploader.ts';

export interface LiveHealth {
  peers: number;
  rttMs: number | null;
  lossPct: number;
  bandwidthLimited: boolean;
  availableOutgoingBps: number | null;
  liveSendBps: number;
}

/**
 * Decides how much upstream bandwidth background uploads may use.
 *
 * Priority order is fixed: (1) local recording quality, (2) the live call, (3) uploads.
 * During a call uploads follow a delay-based AIMD scheme (in the spirit of LEDBAT):
 * grow slowly while the call looks healthy, halve the moment RTT climbs above its
 * baseline, packets are lost, or WebRTC reports it is bandwidth-limited.
 */
export class BandwidthGovernor extends Observable implements RateSource {
  mode: UploadMode = (localStorage.getItem('takehouse.uploadMode') as UploadMode) || 'smart';
  recording = false;
  health: LiveHealth = { peers: 0, rttMs: null, lossPct: 0, bandwidthLimited: false, availableOutgoingBps: null, liveSendBps: 0 };
  /** Current allowance while in a call (bps). */
  callRate = 400_000;
  measuredUploadBps = 0;
  congested = false;
  /** This computer is struggling: hold uploads while recording, so the recording gets the CPU and disk. */
  pressureHold = false;
  private baseRtt: { value: number; at: number }[] = [];
  private lastDecrease = 0;

  setMode(mode: UploadMode) {
    this.mode = mode;
    try {
      localStorage.setItem('takehouse.uploadMode', mode);
    } catch {}
    this.changed();
  }

  setRecording(on: boolean) {
    this.recording = on;
    this.changed();
  }

  setPressureHold(on: boolean) {
    if (this.pressureHold === on) return;
    this.pressureHold = on;
    this.changed();
  }

  allowedBps() {
    if (this.pressureHold && this.recording) return 0;
    if (this.mode === 'max') return Infinity;
    if (this.mode === 'after' && this.recording) return 0;
    if (this.health.peers === 0) return Infinity;
    return this.callRate;
  }

  reportUpload(bytes: number, ms: number) {
    const bps = (bytes * 8) / Math.max(0.001, ms / 1000);
    this.measuredUploadBps = this.measuredUploadBps ? this.measuredUploadBps * 0.7 + bps * 0.3 : bps;
  }

  /** Fed every ~2 s by the mesh with aggregated WebRTC stats. */
  update(h: LiveHealth) {
    this.health = h;
    const now = performance.now();
    if (h.rttMs != null) {
      this.baseRtt.push({ value: h.rttMs, at: now });
      this.baseRtt = this.baseRtt.filter((s) => now - s.at < 120_000);
    }
    const base = this.baseRtt.length ? Math.min(...this.baseRtt.map((s) => s.value)) : null;
    const queueing = h.rttMs != null && base != null && h.rttMs > base + Math.max(40, base * 0.5);
    this.congested = queueing || h.lossPct > 2 || h.bandwidthLimited;

    if (this.congested) {
      if (now - this.lastDecrease > 2500) {
        this.callRate = Math.max(64_000, this.callRate * 0.5);
        this.lastDecrease = now;
      }
    } else if (now - this.lastDecrease > 6000) {
      // Additive increase, but never claim more than half of what the link has shown it can do.
      const ceiling = Math.max(1_000_000, (h.availableOutgoingBps ?? 0) * 0.5, this.measuredUploadBps * 1.5);
      this.callRate = Math.min(ceiling, this.callRate + 150_000);
    }
    this.changed();
  }
}
